import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService, setupGlobalErrorHandler, APIError } from '../services/api-secure';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    // Fonction pour gérer l'expiration de session globale
    const handleSessionExpired = useCallback(() => {
        setUser(null);
        // Rediriger vers la page de connexion si on n'y est pas déjà
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }, []);

    // Configurer le gestionnaire d'erreurs global au montage
    useEffect(() => {
        setupGlobalErrorHandler(handleSessionExpired);
    }, [handleSessionExpired]);

    // Initialiser l'authentification au chargement
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setLoading(true);
                
                // Essayer de récupérer l'utilisateur actuel (cookies automatiques)
                const currentUser = await authService.getCurrentUser();
                
                if (currentUser) {
                    setUser(currentUser);
                } else {
                    setUser(null);
                }
            } catch (error) {
                console.error('Erreur initialisation auth:', error);
                
                // Si erreur de réseau, on garde l'état indéterminé
                if (error.code === 'NETWORK_ERROR') {
                    // On peut afficher un message d'erreur réseau
                    console.warn('Problème de connexion réseau');
                } else {
                    // Autres erreurs = pas connecté
                    setUser(null);
                }
            } finally {
                setLoading(false);
                setIsInitialized(true);
            }
        };

        initializeAuth();
    }, []);

    // Fonction de connexion avec gestion d'erreurs améliorée
    const login = async (email, password) => {
        try {
            setLoading(true);
            const data = await authService.login(email, password);
            setUser(data.user);
            return data.user;
        } catch (error) {
            console.error('Erreur connexion:', error);
            
            // Transformer les erreurs en messages plus user-friendly
            if (error.code === 'INVALID_CREDENTIALS') {
                throw new Error('Email ou mot de passe incorrect');
            } else if (error.code === 'RATE_LIMIT_AUTH') {
                throw new Error('Trop de tentatives de connexion. Réessayez dans 15 minutes.');
            } else if (error.code === 'NETWORK_ERROR') {
                throw new Error('Problème de connexion. Vérifiez votre connexion internet.');
            } else {
                throw new Error('Erreur de connexion: ' + (error.message || 'Erreur inconnue'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Fonction d'inscription avec gestion d'erreurs
    const signUp = async (email, password) => {
        try {
            setLoading(true);
            const data = await authService.register(email, password);
            setUser(data.user);
            return data.user;
        } catch (error) {
            console.error('Erreur inscription:', error);
            
            if (error.code === 'EMAIL_EXISTS') {
                throw new Error('Un compte avec cet email existe déjà');
            } else if (error.status === 400) {
                // Erreurs de validation
                const validationErrors = error.data?.errors;
                if (validationErrors && validationErrors.length > 0) {
                    throw new Error(validationErrors[0].message);
                }
                throw new Error('Données d\'inscription invalides');
            } else if (error.code === 'NETWORK_ERROR') {
                throw new Error('Problème de connexion. Vérifiez votre connexion internet.');
            } else {
                throw new Error('Erreur lors de l\'inscription: ' + (error.message || 'Erreur inconnue'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Fonction de déconnexion
    const logout = async () => {
        try {
            setLoading(true);
            await authService.logout();
        } catch (error) {
            console.error('Erreur déconnexion:', error);
            // Même en cas d'erreur, on déconnecte côté client
        } finally {
            setUser(null);
            setLoading(false);
        }
    };

    // Fonction de déconnexion globale (toutes les sessions)
    const logoutAll = async () => {
        try {
            setLoading(true);
            await authService.logoutAll();
        } catch (error) {
            console.error('Erreur déconnexion globale:', error);
        } finally {
            setUser(null);
            setLoading(false);
        }
    };

    // Fonction pour changer le mot de passe
    const changePassword = async (currentPassword, newPassword) => {
        try {
            await authService.changePassword(currentPassword, newPassword);
            // Le serveur révoque toutes les autres sessions automatiquement
        } catch (error) {
            console.error('Erreur changement mot de passe:', error);
            
            if (error.status === 401) {
                throw new Error('Mot de passe actuel incorrect');
            } else if (error.status === 400) {
                const validationErrors = error.data?.errors;
                if (validationErrors && validationErrors.length > 0) {
                    throw new Error(validationErrors[0].message);
                }
                throw new Error('Format de mot de passe invalide');
            } else {
                throw new Error('Erreur lors du changement de mot de passe');
            }
        }
    };

    // Fonction pour récupérer les sessions actives
    const getSessions = async () => {
        try {
            return await authService.getSessions();
        } catch (error) {
            console.error('Erreur récupération sessions:', error);
            throw new Error('Impossible de récupérer les sessions actives');
        }
    };

    // Fonction pour mettre à jour les informations utilisateur
    const updateUser = (updatedUser) => {
        setUser(updatedUser);
    };

    // Fonction pour vérifier si l'utilisateur est connecté
    const checkAuthStatus = async () => {
        try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
                return true;
            } else {
                setUser(null);
                return false;
            }
        } catch (error) {
            console.error('Erreur vérification auth:', error);
            setUser(null);
            return false;
        }
    };

    // Fonction pour rafraîchir les tokens (normalement automatique)
    const refreshSession = async () => {
        try {
            const user = await authService.refreshTokens();
            if (user) {
                setUser(user);
                return true;
            } else {
                setUser(null);
                return false;
            }
        } catch (error) {
            console.error('Erreur refresh session:', error);
            setUser(null);
            return false;
        }
    };

    const value = {
        // État
        user,
        loading,
        isInitialized,
        isAuthenticated: !!user,
        
        // Actions de base
        login,
        signUp,
        logout,
        logoutAll,
        
        // Actions avancées
        changePassword,
        getSessions,
        updateUser,
        checkAuthStatus,
        refreshSession
    };

    return (
        <AuthContext.Provider value={value}>
            {isInitialized && children}
        </AuthContext.Provider>
    );
}

// Hook personnalisé avec vérifications
export const useAuth = () => {
    const context = useContext(AuthContext);
    
    if (context === undefined) {
        throw new Error('useAuth doit être utilisé dans un AuthProvider');
    }
    
    return context;
};
